import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Clock, AlertCircle, ArrowLeft, Square, RotateCcw, X, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MockExam = ({ progress, updateProgress }) => {
  const { stage, subject } = useParams();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [timeLeft, setTimeLeft] = useState(45 * 60); // 45 minutes
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showStopDialog, setShowStopDialog] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [examResults, setExamResults] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [examStopped, setExamStopped] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadQuestions();
  }, [stage, subject]);

  useEffect(() => {
    if (examStopped || showResults) return; // Don't run timer if exam stopped or showing results
    
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmit(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [examStopped, showResults]);

  const loadQuestions = async () => {
    try {
      const testType = `stage${stage}_${subject}`;
      const response = await axios.get(`${API}/questions/${testType}`);
      setQuestions(response.data.questions);
      setLoading(false);
    } catch (error) {
      console.error('Error loading questions:', error);
      toast.error('Failed to load exam questions');
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId, answer) => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSubmit = async (autoSubmit = false, forceStop = false) => {
    const answerList = Object.entries(answers).map(([question_id, selected_answer]) => ({
      question_id,
      selected_answer: parseInt(selected_answer)
    }));

    // Allow submission with partial answers if force stopped or auto-submitted
    if (answerList.length !== questions.length && !autoSubmit && !forceStop) {
      toast.error('Please answer all questions before submitting');
      return;
    }

    setSubmitting(true);
    setExamStopped(true);
    
    try {
      const timeTaken = (45 * 60) - timeLeft;
      const response = await axios.post(`${API}/submit-test`, {
        stage: parseInt(stage),
        subject,
        answers: answerList,
        time_taken: timeTaken
      });

      const scoreKey = `stage${stage}_${subject}_score`;
      const updates = {
        [scoreKey]: response.data.percentage,
        lastTestResult: response.data
      };

      // Check if Stage 1 is passed
      if (stage === '1') {
        const mathsScore = subject === 'maths' ? response.data.percentage : progress?.stage1_maths_score;
        const englishScore = subject === 'english' ? response.data.percentage : progress?.stage1_english_score;
        
        if (mathsScore >= 70 && englishScore >= 70) {
          updates.stage1_passed = true;
        }
      }

      updateProgress(updates);
      
      // Store results for display
      setExamResults({
        ...response.data,
        questionsAnswered: answerList.length,
        totalQuestions: questions.length,
        wasForceStop: forceStop,
        wasAutoSubmit: autoSubmit
      });
      setShowResults(true);
      
      if (autoSubmit) {
        toast.error('Time\'s up! Test auto-submitted.');
      } else if (forceStop) {
        toast.info(`Exam stopped. ${answerList.length} questions answered.`);
      } else {
        toast.success('Test submitted successfully!');
      }
    } catch (error) {
      console.error('Error submitting test:', error);
      toast.error('Failed to submit test');
      setSubmitting(false);
      setExamStopped(false);
    }
  };

  const handleStop = () => {
    setShowStopDialog(true);
  };

  const confirmStop = () => {
    setShowStopDialog(false);
    handleSubmit(false, true); // Force stop
  };

  const handleRetake = () => {
    // Reset all state
    setAnswers({});
    setTimeLeft(45 * 60);
    setShowResults(false);
    setExamResults(null);
    setExamStopped(false);
    setSubmitting(false);
    loadQuestions();
  };

  const handleClose = () => {
    navigate('/categories');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading exam...</p>
        </div>
      </div>
    );
  }

  const progressPercent = (Object.keys(answers).length / questions.length) * 100;
  const isTimeRunningOut = timeLeft < 300; // less than 5 minutes

  // Results View
  if (showResults && examResults) {
    const isPassed = examResults.percentage >= 70;
    return (
      <div className="min-h-screen p-4 md:p-8" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div className="max-w-2xl mx-auto">
          <Card className="shadow-2xl" data-testid="exam-results-card">
            <CardHeader className={`text-white rounded-t-lg ${isPassed ? 'bg-gradient-to-r from-green-500 to-emerald-600' : 'bg-gradient-to-r from-orange-500 to-red-500'}`}>
              <div className="text-center py-4">
                {isPassed ? (
                  <CheckCircle className="h-16 w-16 mx-auto mb-4" />
                ) : (
                  <XCircle className="h-16 w-16 mx-auto mb-4" />
                )}
                <CardTitle className="text-3xl font-bold mb-2">
                  {examResults.wasForceStop ? 'Exam Stopped' : isPassed ? 'Well Done!' : 'Keep Practicing!'}
                </CardTitle>
                <p className="text-white/90">
                  Stage {stage} - {subject === 'maths' ? 'Mathematics' : 'English'}
                </p>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              {/* Score Display */}
              <div className="text-center py-6 bg-gray-50 rounded-xl">
                <p className="text-sm text-gray-500 mb-2">Your Score</p>
                <p className={`text-6xl font-bold ${isPassed ? 'text-green-600' : 'text-orange-600'}`}>
                  {examResults.percentage}%
                </p>
                <p className="text-gray-600 mt-2">
                  {examResults.correct_count} out of {examResults.total_questions} correct
                </p>
              </div>

              {/* Questions Answered Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-100 p-2 rounded-full">
                    <AlertCircle className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-blue-900">Questions Answered</p>
                    <p className="text-sm text-blue-700">
                      {examResults.questionsAnswered} of {examResults.totalQuestions} questions
                      {examResults.wasForceStop && ' (exam was stopped early)'}
                      {examResults.wasAutoSubmit && ' (time ran out)'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Pass/Fail Message */}
              <div className={`rounded-xl p-4 ${isPassed ? 'bg-green-50 border border-green-200' : 'bg-orange-50 border border-orange-200'}`}>
                <p className={`font-semibold ${isPassed ? 'text-green-900' : 'text-orange-900'}`}>
                  {isPassed ? '✅ You passed! Great job!' : '📚 You need 70% to pass. Keep practicing!'}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 pt-4">
                <Button
                  onClick={handleRetake}
                  variant="outline"
                  className="flex-1 border-purple-300 text-purple-600 hover:bg-purple-50"
                  data-testid="retake-exam-btn"
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Retake Exam
                </Button>
                <Button
                  onClick={handleClose}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                  data-testid="close-exam-btn"
                >
                  <X className="h-4 w-4 mr-2" />
                  Close
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-8" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      <div className="max-w-4xl mx-auto">
        {/* Top Navigation Bar */}
        <div className="flex items-center justify-between mb-4">
          <Button
            onClick={() => navigate(-1)}
            variant="outline"
            className="bg-white/10 border-white/20 text-white hover:bg-white/20"
            data-testid="go-back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Go Back
          </Button>
          <Button
            onClick={handleStop}
            variant="destructive"
            className="bg-red-500 hover:bg-red-600"
            data-testid="stop-exam-btn"
          >
            <Square className="h-4 w-4 mr-2" />
            Stop Exam
          </Button>
        </div>

        <Card className="shadow-2xl animate-fade-in" data-testid="exam-card">
          <CardHeader className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-t-lg sticky top-0 z-10">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl font-bold">
                  Stage {stage} - {subject === 'maths' ? 'Mathematics' : 'English'}
                </CardTitle>
                <p className="text-sm text-white/90 mt-1">25 Questions</p>
              </div>
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${isTimeRunningOut ? 'bg-red-500 animate-pulse' : 'bg-white/20'}`}>
                <Clock className="h-5 w-5" />
                <span className="font-bold text-lg" data-testid="timer">{formatTime(timeLeft)}</span>
              </div>
            </div>
            <div className="mt-4">
              <div className="flex justify-between text-sm mb-2">
                <span>Progress</span>
                <span className="font-semibold">{Object.keys(answers).length} / {questions.length}</span>
              </div>
              <Progress value={progressPercent} className="h-2 bg-white/30" />
            </div>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            {questions.map((q, index) => (
              <Card key={q.id} className="border-2 hover:border-purple-300 transition-colors" data-testid={`exam-question-${q.id}`}>
                <CardHeader>
                  <CardTitle className="text-lg font-semibold">
                    <span className="text-purple-600 mr-2">Q{index + 1}.</span>
                    {q.question}
                  </CardTitle>
                  <p className="text-xs text-gray-500">Topic: {q.topic}</p>
                </CardHeader>
                <CardContent>
                  <RadioGroup value={answers[q.id]?.toString()} onValueChange={(val) => handleAnswerChange(q.id, val)}>
                    {q.options.map((option, idx) => (
                      <div key={idx} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors" data-testid={`exam-question-${q.id}-option-${idx}`}>
                        <RadioGroupItem value={idx.toString()} id={`${q.id}-${idx}`} />
                        <Label htmlFor={`${q.id}-${idx}`} className="flex-1 cursor-pointer">{option}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                </CardContent>
              </Card>
            ))}

            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="flex items-start gap-3 p-4">
                <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                <div className="flex-1">
                  <p className="font-semibold text-amber-900">Before you submit:</p>
                  <ul className="text-sm text-amber-800 mt-1 list-disc list-inside">
                    <li>Ensure all questions are answered</li>
                    <li>You need 70% to pass Stage 1</li>
                    <li>Review your answers if time permits</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            <div className="flex gap-4">
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
                className="flex-1"
                data-testid="back-to-dashboard"
              >
                Back to Dashboard
              </Button>
              <Button
                onClick={() => setShowConfirmDialog(true)}
                disabled={Object.keys(answers).length !== questions.length || submitting}
                className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                data-testid="submit-exam-button"
              >
                {submitting ? 'Submitting...' : 'Submit Test'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent data-testid="submit-confirmation-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Submit Test?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to submit your test? You won't be able to change your answers after submission.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-submit">Review Answers</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleSubmit(false)} data-testid="confirm-submit">
              Yes, Submit
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Stop Exam Confirmation Dialog */}
      <AlertDialog open={showStopDialog} onOpenChange={setShowStopDialog}>
        <AlertDialogContent data-testid="stop-confirmation-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">Stop Exam?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to stop this exam? You have answered {Object.keys(answers).length} of {questions.length} questions. 
              Your current answers will be submitted and scored.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-stop">Continue Exam</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmStop} 
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-stop"
            >
              Yes, Stop & Submit
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MockExam;
